import {
  BadRequestError,
  ExchangeIdPattern,
  IntegrationExchangeDisplayByType,
  IntegrationExchangeDispositionDisplay,
  IntegrationExchangeTypeEnum,
  IntegrationInvoice,
  IntegrationInvoiceFilterType,
  IntegrationSummaryType,
  InvoiceStatusEnum,
  STRING_ANY_HASH,
  ValidExchangeFunctionsEnum,
  clog,
  mimeTypeByExtension,
} from '@chiroup/core';
import { ChiroUpAPI } from '@chiroup/client-core';

type exchangeArgsType = {
  f: string;
  clinicId?: number;
  id?: string;
  days?: string | undefined | null;
  disposition?: string;
  billingProfileId?: string | null;
  trace?: boolean;
  types?: IntegrationExchangeTypeEnum[];
};

const validExchangeFunctions: STRING_ANY_HASH = {
  [ValidExchangeFunctionsEnum.Activity]: {
    validateInput: (args: exchangeArgsType, issues: string[]) => {
      const { id } = args;
      if (!id || typeof id !== 'string') issues.push(`Invalid id: ${id}`);
      if (!ExchangeIdPattern.test(`${id}`)) issues.push(`Invalid id: ${id}`);
      return issues.length === 0;
    },
    queryParams: (args: exchangeArgsType, params: string[]) => {
      const { id } = args;
      params.push(`id=${id}`);
    },
  },
  [ValidExchangeFunctionsEnum.UIList]: {
    validateInput: (args: exchangeArgsType, issues: string[]) => {
      const { types = [] } = args;
      if (types.length === 0) {
        issues.push(`No types provided.`);
      } else {
        types.forEach((t) => {
          if (!IntegrationExchangeDisplayByType[t]) {
            issues.push(`Invalid type: ${t}`);
          }
        });
      }
      return issues.length === 0;
    },
    queryParams: (args: exchangeArgsType, params: string[]) => {
      const { types = [] } = args;
      params.push(`types=${types.join('+')}`);
    },
  },
  [ValidExchangeFunctionsEnum.ByType]: {
    validateInput: (args: exchangeArgsType, issues: string[]) => {
      const { types = [] } = args;
      if (types.length === 0) {
        issues.push(`No types provided.`);
      } else {
        types.forEach((t) => {
          if (!IntegrationExchangeDisplayByType[t]) {
            issues.push(`Invalid type: ${t}`);
          }
        });
      }
      return issues.length === 0;
    },
    queryParams: (args: exchangeArgsType, params: string[]) => {
      const { types = [] } = args;
      params.push(`types=${types.join('+')}`);
    },
  },
  [ValidExchangeFunctionsEnum.Summary]: {
    validateInput: (args: exchangeArgsType, issues: string[]) => {
      const { days, disposition } = args;
      const numericDays = Number(days);
      if (days !== 'all' && days !== null) {
        if (isNaN(numericDays) || numericDays < 1) {
          issues.push(`Invalid days: ${days}`);
        }
      }
      if (disposition && disposition !== 'any') {
        if (!IntegrationExchangeDispositionDisplay[disposition]) {
          issues.push(`Invalid disposition: ${disposition}`);
        }
      }
      return issues.length === 0;
    },
    queryParams: (args: exchangeArgsType, params: string[]) => {
      const { days, disposition } = args;
      if (days) params.push(`days=${days}`);
      if (disposition) params.push(`disposition=${disposition}`);
    },
  },
  [ValidExchangeFunctionsEnum.Single]: {
    validateInput: (args: exchangeArgsType, issues: string[]) => {
      const { id } = args;
      if (!id || typeof id !== 'string') issues.push(`Invalid id: ${id}`);
      if (!ExchangeIdPattern.test(`${id}`)) issues.push(`Invalid id: ${id}`);
      return issues.length === 0;
    },
    queryParams: (args: exchangeArgsType, params: string[]) => {
      const { id } = args;
      params.push(`id=${id}`);
    },
  },
};

const integrationServiceFactory = () => {
  /**
   * Given a clinicId, this returns a summary of the file/document
   * exchanges for that clinic.
   *
   * @param param0
   * @returns
   */
  const exchange = async (args: exchangeArgsType) => {
    const {
      f,
      clinicId,
      id,
      billingProfileId,
      days,
      disposition,
      trace = false,
      types = [],
    } = args;
    const issues = [];
    if (!f) issues.push('A function must be specified.');
    if (!clinicId) issues.push('A clinicId is required.');
    if (!validExchangeFunctions[f]) {
      issues.push(`Invalid exchange function: ${f}.`);
    }
    if (!billingProfileId && f === ValidExchangeFunctionsEnum.ByType) {
      issues.push('A billingProfileId is required.');
    }
    if (issues.length > 0) throw new BadRequestError(issues.join(' '));
    if (!validExchangeFunctions[f].validateInput(args, issues)) {
      throw new BadRequestError(issues.join(' '));
    }

    if (trace) {
      clog({
        integrationService_exchange: {
          clinicId,
          id,
          days,
          disposition,
          f,
          trace,
          types,
        },
      });
    }

    const qargs = [`f=${f}`];
    if (billingProfileId) qargs.push(`billingProfileId=${billingProfileId}`);
    if (trace) qargs.push('trace=1');

    validExchangeFunctions[f].queryParams(args, qargs);

    const q = qargs.length > 0 ? `?${qargs.join('&')}` : '';
    const res: IntegrationSummaryType = await ChiroUpAPI.get(
      'api',
      `/transactions/${clinicId}/integration/exchange${q}`,
      {},
    );
    if (trace) clog({ integrationService_exchangeSummary: { res } });

    return res ?? null;
  };

  const invoicesByBillingKey = async ({
    clinicId,
    trace,
    billingKey,
  }: {
    clinicId?: number;
    trace?: boolean;
    billingKey?: string;
  }) => {
    if (!clinicId) throw new Error('A clinic id is required.');
    if (!billingKey) {
      throw new Error(`A billing key is required.`);
    }

    if (trace) {
      clog({
        integrationService_iinvoicesByBillngKey: { clinicId, billingKey },
      });
    }

    const res: IntegrationInvoice[] = await ChiroUpAPI.post(
      'api',
      `/transactions/${clinicId}/integration/invoice${trace ? '?trace=1' : ''}`,
      { body: { billingKey } },
    );
    if (trace) clog({ integrationService_invoicesByBillngKey: { res } });

    return res ?? null;
  };

  const invoicesByStatus = async ({
    clinicId,
    trace,
    status = [
      InvoiceStatusEnum.Waiting,
      InvoiceStatusEnum.Pending,
      InvoiceStatusEnum.Unsubmitted,
    ],
    billingProfileId,
    days,
    startDate,
    endDate,
    patientId,
    renderingProviderId,
    payorId,
  }: IntegrationInvoiceFilterType & {
    clinicId?: number;
    trace?: boolean;
  }) => {
    if (!clinicId) throw new Error('clinicId is required.');
    /**
     * TODO: Put in some sane validation. If they want a huge date range,
     * we should probably make them specify only certain statuses.
     */

    const args = {
      clinicId,
      billingProfileId,
      status,
      days,
      startDate,
      endDate,
      patientId,
      renderingProviderId,
      payorId,
    };
    if (trace) {
      clog({
        integrationService_invoicesByStatus: { ...args },
      });
    }

    const res: { count: number; data: IntegrationInvoice[]; page: number } =
      await ChiroUpAPI.post(
        'api',
        `/transactions/${clinicId}/integration/invoice${
          trace ? '?trace=1' : ''
        }`,
        {
          body: { ...args },
        },
      );
    if (trace) clog({ integrationService_invoicesByStatus: { res } });

    return res ?? null;
  };

  const list = async ({
    clinicId,
    trace,
  }: {
    clinicId?: number;
    trace?: boolean;
  }) => {
    if (!clinicId) throw new Error('clinicId is required');

    const res: STRING_ANY_HASH = await ChiroUpAPI.get(
      'api',
      `/transactions/${clinicId}/integration`,
      {},
    );
    if (trace) clog({ integrationService: { res } });
    return res ?? null;
  };

  const settingsByClinicId = async ({
    clinicId,
    trace,
  }: {
    clinicId?: number;
    trace?: boolean;
  }) => {
    if (!clinicId) throw new Error('clinicId is required');

    const res: STRING_ANY_HASH = await ChiroUpAPI.get(
      'api',
      `/transactions/${clinicId}/integration${trace ? '?trace=1' : ''}`,
      {},
    );
    if (trace) clog({ integrationService: { res } });
    return res ?? null;
  };

  const remote = async ({
    clinicId,
    script,
    trace,
  }: {
    clinicId?: number;
    trace?: boolean;
    script?: STRING_ANY_HASH[];
  }) => {
    if (!clinicId) throw new Error('clinicId is required.');
    if (trace) {
      clog({ integration_service_ts: { clinicId, script, trace } });
    }

    const res: STRING_ANY_HASH = await ChiroUpAPI.post(
      'api',
      `/transactions/${clinicId}/integration/remote${trace ? '?trace=1' : ''}`,
      { body: script },
    );
    if (trace) clog({ integrationService_remote: { res } });
    return res ?? null;
  };

  const upload = async ({
    clinicId,
    profileId,
    file,
    trace,
  }: {
    clinicId: number;
    profileId: number;
    trace?: boolean;
    file?: any;
  }) => {
    if (!clinicId) throw new Error('clinicId is required.');
    const name = file?.name ?? '-bogus-.-bad',
      pcs = name.split('.'),
      size = file?.size ?? -1,
      basename = pcs[0] ?? '-bogus-',
      type = pcs.length === 2 ? pcs[1] : '-bogus-',
      body = {
        body: {
          name,
          profileId,
          basename,
          type,
          size,
          mimeType: mimeTypeByExtension(type),
        },
      };

    if (trace) {
      clog({
        integration_service_upload_in: { clinicId, file, trace, body },
      });
    }

    const res: STRING_ANY_HASH = await ChiroUpAPI.post(
      'api',
      `/transactions/${clinicId}/integration/upload${trace ? '?trace=1' : ''}`,
      body,
    );
    if (trace) clog({ integration_service_upload_resp: { res } });
    return res ?? null;
  };

  const download = async ({
    clinicId,
    exchangeId,
    trace = false,
  }: {
    clinicId?: number;
    exchangeId?: string;
    trace?: boolean;
  }): Promise<string | null> => {
    if (trace) {
      clog({
        integration_service_download_in: { clinicId, exchangeId, trace },
      });
    }

    if (!clinicId) throw new Error('clinicId is required.');
    if (!exchangeId) throw new Error('An exchange id is required.');

    const args = [];
    if (exchangeId) args.push(`exchangeId=${exchangeId}`);
    if (trace) args.push('trace=1');

    const res: string = await ChiroUpAPI.get(
      'api',
      `/transactions/${clinicId}/integration/download${
        args.length > 0 ? `?${args.join('&')}` : ''
      }`,
      {},
    );
    return res ?? null;
  };

  const parse = async ({
    clinicId,
    exchangeId,
    key,
    trace = false,
  }: {
    clinicId?: number;
    exchangeId?: string;
    key?: string;
    trace?: boolean;
  }): Promise<string | null> => {
    if (!clinicId) throw new Error('clinicId is required.');
    if (!key) throw new Error('key is required.');

    if (trace) {
      clog({
        integration_service_parse_in: { clinicId, key, trace },
      });
    }

    const res: string = await ChiroUpAPI.post(
      'api',
      `/transactions/${clinicId}/integration/parse${trace ? '?trace=1' : ''}`,
      { body: { key, exchangeId } },
    );

    if (trace) {
      clog({
        integration_service_parse_out: { res },
      });
    }
    return res ?? null;
  };

  return {
    download,
    exchange,
    invoicesByStatus,
    invoicesByBillingKey,
    list,
    parse,
    remote,
    settingsByClinicId,
    upload,
  };
};

export const integrationService = integrationServiceFactory();
